import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowRight, FiMaximize, FiLayout, FiTruck, FiWifi, 
  FiStar, FiClock, FiMapPin, FiDroplet, FiBriefcase, 
  FiActivity, FiSlash, FiSquare, FiWind, FiZap, FiHome, 
  FiAlertTriangle, FiShield, FiBell, FiCoffee, FiRefreshCw, FiBox, FiCpu, FiUsers, FiMap, FiSmile,
  FiTv
} = FiIcons;

const Services = () => {
  const highlights = [
    { icon: FiWind, title: 'Heating & AC' },
    { icon: FiDroplet, title: 'Soap & Shampoo' },
    { icon: FiZap, title: 'Hair Dryer & Iron' },
    { icon: FiHome, title: 'Bed & Bath Essentials' },
    { icon: FiBell, title: 'Smoke & CO Alarm' },
    { icon: FiShield, title: 'Fire Extinguisher' },
    { icon: FiTv, title: 'Smart TV' },
    { icon: FiLayout, title: 'Kitchen Utensils' },
    { icon: FiCoffee, title: 'Coffee Maker' },
    { icon: FiRefreshCw, title: 'Dishwasher' },
    { icon: FiMaximize, title: 'Microwave' },
    { icon: FiBox, title: 'Refrigerator' },
    { icon: FiTruck, title: 'Free Parking' },
    { icon: FiWifi, title: 'Free Wi-Fi' },
    { icon: FiStar, title: 'Pool Views' },
    { icon: FiClock, title: 'Flexible Stays' },
    { icon: FiMapPin, title: 'Walk To I-Drive' },
    { icon: FiDroplet, title: 'Guest Laundry on 4th Floor' },
    { icon: FiBriefcase, title: 'Business Center' },
    { icon: FiActivity, title: 'Gym' },
    { icon: FiSlash, title: 'No Pets Allowed' },
    { icon: FiSquare, title: '542 sq. ft. Suite' },
    { icon: FiMaximize, title: 'Sleeps 6' }
  ];

  return (
    <section className="py-32 bg-[#faf8f5]" id="services">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Welcome Section - 7601 Canada Avenue */}
        <div className="mb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mb-16"
          >
            <span className="text-accent-orange font-black tracking-[0.3em] uppercase text-[10px] mb-6 block underline underline-offset-8 decoration-2">Prime Location</span>
            <h2 style={{ fontSize: '43px' }} className="text-5xl md:text-7xl font-serif font-black text-black uppercase tracking-tighter leading-[0.95] mb-8">
              Modern Boutique Suites in the Heart of Orlando
            </h2>
          </motion.div>

          {/* New Family Focused Dark Grey Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#1a1a1a] rounded-[40px] p-8 md:p-16 mb-24 shadow-2xl border border-white/5"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-accent-orange/10 rounded-2xl flex items-center justify-center mb-6">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-accent-orange" />
                </div>
                <h3 className="text-xl font-serif font-black text-white uppercase tracking-tight">
                  A Suite Designed for Families
                </h3>
                <p className="text-white/60 text-sm leading-relaxed font-medium">
                  Our boutique suites give families the space, comfort, and convenience you simply don’t get in a standard hotel room. Enjoy a full kitchen, separate living and sleeping areas, and a quiet, modern space to unwind after a big day at the parks.
                </p>
              </div>

              <div className="space-y-4">
                <div className="w-12 h-12 bg-accent-orange/10 rounded-2xl flex items-center justify-center mb-6">
                  <SafeIcon icon={FiMap} className="w-6 h-6 text-accent-orange" />
                </div>
                <h3 className="text-xl font-serif font-black text-white uppercase tracking-tight">
                  Walkable, Safe, and Close to Everything
                </h3>
                <p className="text-white/60 text-sm leading-relaxed font-medium">
                  We’re located just minutes from Universal, ICON Park, and dozens of family‑friendly restaurants and attractions. No long drives. No complicated commutes. Just easy, stress‑free fun.
                </p>
              </div>

              <div className="space-y-4">
                <div className="w-12 h-12 bg-accent-orange/10 rounded-2xl flex items-center justify-center mb-6">
                  <SafeIcon icon={FiSmile} className="w-6 h-6 text-accent-orange" />
                </div>
                <h3 className="text-xl font-serif font-black text-white uppercase tracking-tight">
                  Comfort That Makes Travel Easier
                </h3>
                <p className="text-white/60 text-sm leading-relaxed font-medium">
                  From fast WiFi and smart TVs to a full kitchen and cozy living room, our suites are designed to make your stay feel effortless — whether you’re traveling with toddlers, teens, or grandparents.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            {/* Left Column: Why Choose Us */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <div className="space-y-6">
                <h3 className="text-2xl font-serif font-black text-black uppercase tracking-tight flex items-center gap-3">
                  <span className="text-blue-500 text-3xl">🏡</span> Why Choose Us
                </h3>
                <ul className="text-black/70 text-lg leading-relaxed font-medium space-y-4 pl-4">
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Fully renovated, modern interiors</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Spacious layouts with separate living + sleeping areas</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Full kitchens (rare for this area)</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Fast WiFi + workspace</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Smart TVs with streaming</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Free parking</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Walkable to restaurants, cafés, and shops</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Minutes to Universal, Epic Universe & OCCC</li>
                  <li className="relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-accent-orange before:text-2xl before:leading-none">Professionally cleaned and maintained</li>
                </ul>
                <div className="pt-4 space-y-4">
                  <p className="text-black/50 italic text-sm font-bold uppercase tracking-wider">* A 10-minute drive to the Orange County Convention Center (OCCC)</p>
                  <p className="text-black/80 text-lg font-medium leading-relaxed">
                    Our suites offer the comfort of a modern apartment with the convenience of being close to International Drive for shopping and dining.
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Right Column: Key Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Classic Orlando Setting */}
              <div className="bg-black/5 p-8 rounded-[32px] space-y-4">
                <h3 className="text-xl font-serif font-black text-black uppercase tracking-tight flex items-center gap-3">
                  <span className="text-yellow-500 text-2xl">⭐</span> Classic Orlando, Modern Inside
                </h3>
                <p className="text-black/70 text-base leading-relaxed font-medium">
                  The property has a timeless Orlando look on the outside, while each of our privately owned suites has been fully updated for today’s traveler. Guests love the combination of classic charm and modern interiors — a true hidden gem in the heart of I‑Drive.
                </p>
              </div>

              {/* Business Travelers */}
              

              {/* Prime Location */}
              <div className="bg-black text-white p-8 rounded-[32px] space-y-6 shadow-2xl">
                <h3 className="text-xl font-serif font-black text-white uppercase tracking-tight flex items-center gap-3">
                  <span className="text-accent-orange text-2xl">⭐</span> Prime I-Drive Location
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { time: '5 mins', dest: 'Universal & Epic Universe' },
                    { time: '3 mins', dest: 'ICON Park' },
                    { time: '5 mins', dest: 'Convention Center (OCCC)' },
                    { time: '10-12 mins', dest: 'SeaWorld' },
                    { time: '15-18 mins', dest: 'Disney' },
                    { time: '20 mins', dest: 'MCO Airport' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between border-b border-white/10 pb-2">
                      <span className="text-[10px] font-black uppercase tracking-widest text-white/50">{item.dest}</span>
                      <span className="text-accent-orange font-bold text-xs">{item.time}</span>
                    </div>
                  ))}
                </div>
                <p className="text-white/60 text-xs font-medium leading-relaxed">
                  Walk to restaurants, cafés, bars, and convenience stores — everything you need is right here.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Amenities Section */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="pt-24 border-t border-black/5"
        >
          <div className="mb-16 text-center">
            <span className="text-accent-orange font-black tracking-[0.3em] uppercase text-[10px] mb-4 block">Features</span>
            <h3 style={{ fontSize: '52px', textAlign: 'center' }} className="text-4xl md:text-5xl font-serif font-black text-black uppercase tracking-tighter">
              AMENITIES
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-y-16 gap-x-12 max-w-7xl mx-auto">
            {highlights.map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-black text-white rounded-[32px] flex items-center justify-center mb-6 transition-transform duration-500 hover:-translate-y-2 hover:shadow-xl hover:bg-accent-orange">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-black leading-tight max-w-[120px]">{item.title}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Clickable Google Map Section */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 pt-24 border-t border-black/5"
        >
          <div className="mb-12 text-center md:text-left">
            <span className="text-accent-orange font-black tracking-[0.3em] uppercase text-[10px] mb-4 block">Location</span>
            <h3 style={{ fontSize: '51px', textAlign: 'center' }} className="text-4xl md:text-5xl font-serif font-black text-black uppercase tracking-tighter">
              OUR LOCATION
            </h3>
            <p className="text-black/40 font-bold uppercase tracking-widest text-xs mt-4">7601 Canada Ave, Orlando, FL 32819</p>
          </div>
          
          <a 
            href="https://maps.google.com/?q=I-Drive+Orlando+7601+Canada+Ave+Orlando+FL+32819" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block relative w-full h-[450px] rounded-[40px] overflow-hidden group cursor-pointer shadow-2xl border border-black/5"
          >
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.412462391093!2d-81.4709216!3d28.4552097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e77e8a3a3a3a3b%3A0x6b9a9a9a9a9a9a9a!2s7601%20Canada%20Ave%2C%20Orlando%2C%20FL%2032819!5e0!3m2!1sen!2sus!4v1710345678901!5m2!1sen!2sus" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              className="grayscale group-hover:grayscale-0 transition-all duration-700 pointer-events-none"
            ></iframe>
            
            {/* Clickable Overlay */}
            <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500 flex items-center justify-center">
              <div className="bg-black text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-3 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 shadow-2xl">
                Open in Google Maps <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </div>
            </div>
            
            <div className="absolute bottom-10 left-10 bg-white/90 backdrop-blur-md text-black px-6 py-3 rounded-xl text-[8px] font-black uppercase tracking-widest group-hover:bg-black group-hover:text-white transition-all">
              7601 Canada Ave, Orlando
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;